import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, X, Radio, Film, Crown, Pin } from "lucide-react";
import { GatePill, TokenGatePill } from "@/components/common/TrustBadge";
import { MICROTX_ENABLED } from "@/config";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { cn } from "@/lib/utils";

// Per-topic unread pill for a BLOQ list row — surfaces WHICH topic has new activity before opening,
// so members jump straight to the active discussion. Only fetches topics when the BLOQ has unread.
function BloqTopicUnread({ bloqId, unread }) {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["bloq-topics", bloqId],
    queryFn: () => api.bloqTopics(bloqId),
    enabled: unread > 0,
    staleTime: 20000,
  });
  const hot = (data?.topics || [])
    .filter((t) => t.unread > 0)
    .sort((a, b) => b.unread - a.unread);

  // No topic-level breakdown available → fall back to a plain unread count badge.
  if (hot.length === 0) {
    if (unread > 0) {
      return (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-bloq-mint px-1.5 text-[11px] font-bold text-bloq-bg" data-testid={`bloq-unread-${bloqId}`}>
          {unread}
        </span>
      );
    }
    return null;
  }

  const top = hot[0];
  const extra = hot.length - 1;
  return (
    <div className="flex items-center gap-1" data-testid={`bloq-topic-unread-${bloqId}`}>
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); navigate(`/app/bloqs/${bloqId}?topic=${top.id}`); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); navigate(`/app/bloqs/${bloqId}?topic=${top.id}`); } }}
        className="inline-flex max-w-[130px] cursor-pointer items-center gap-1 rounded-full border border-bloq-mint/40 bg-bloq-mint/15 px-1.5 py-0.5 text-[10px] font-bold text-bloq-mint transition-colors hover:bg-bloq-mint/25"
        title={`Jump to ${top.name} · ${top.unread} new`}
        data-testid={`bloq-hot-topic-${bloqId}`}
      >
        <span>{top.emoji || "#"}</span>
        <span className="truncate">{top.name}</span>
        <span className="rounded-full bg-bloq-mint px-1 text-[9px] text-bloq-bg">{top.unread}</span>
      </span>
      {extra > 0 && <span className="text-[10px] font-semibold text-bloq-muted" data-testid={`bloq-topic-extra-${bloqId}`}>+{extra}</span>}
    </div>
  );
}

export function BloqEmoji({ emoji, icon, size = "md" }) {
  const dim = size === "lg" ? "h-14 w-14" : "h-12 w-12";
  const src = icon ? (icon.startsWith("data:") ? icon : `data:image/jpeg;base64,${icon}`) : null;
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className={cn("shrink-0 rounded-2xl border border-white/10 object-cover", dim)}
        data-testid="bloq-icon-img"
      />
    );
  }
  return (
    <div className={cn("flex shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-bloq-s2", dim, size === "lg" ? "text-2xl" : "text-xl")}>
      {emoji || "🛡️"}
    </div>
  );
}

// Owners of a growing, not-yet-monetized BLOQ get a subtle "Earn" nudge once the
// community grows past just the founder. Dismissible per-bloq.
export function EarnNudge({ bloqId, name, memberCount, onCta }) {
  const key = `bloq_earn_nudge_${bloqId}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(key) === "1");
  if (dismissed) return null;
  const dismiss = (e) => {
    e?.stopPropagation?.();
    localStorage.setItem(key, "1");
    setDismissed(true);
  };
  return (
    <div className="flex items-center gap-2.5 border-b border-bloq-purple/20 bg-bloq-purple/[0.07] px-4 py-2.5" data-testid="earn-nudge">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-bloq-purple/20 text-bloq-purple">
        <Sparkles className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">Your community is growing 🎉</div>
        <div className="truncate text-xs text-bloq-muted">
          {memberCount} members in {name} — monetize it and keep 85% of every membership.
        </div>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onCta(); }}
        className="shrink-0 rounded-full bg-bloq-purple/20 px-3 py-1.5 text-xs font-bold text-bloq-purple transition-colors hover:bg-bloq-purple/30"
        data-testid="earn-nudge-cta"
      >
        Set up earnings
      </button>
      <button onClick={dismiss} className="shrink-0 rounded-full p-1 text-bloq-muted hover:text-white" data-testid="earn-nudge-dismiss" title="Dismiss">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function LiveNowRow({ bloq, onJoin }) {
  const r = bloq.active_room;
  return (
    <button
      onClick={() => onJoin(bloq)}
      className="flex w-full items-center gap-3 rounded-2xl border border-destructive/30 bg-destructive/10 p-3 text-left transition-colors hover:bg-destructive/15"
      data-testid={`live-now-${bloq.id}`}
    >
      <BloqEmoji emoji={bloq.emoji} icon={bloq.icon_b64} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-semibold">{bloq.name}</span>
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-destructive/20 px-1.5 py-0.5 text-[9px] font-bold uppercase text-destructive">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-destructive" /> Live
          </span>
        </div>
        <p className="mt-0.5 truncate text-sm text-bloq-muted">
          {r?.title || "Live room"} · {r?.speaker_count ?? 0} on stage · {r?.listener_count ?? 0} listening
        </p>
      </div>
      <span className="flex shrink-0 items-center gap-1 rounded-full bloq-gradient px-3 py-1.5 text-xs font-bold text-bloq-bg">
        <Radio className="h-3.5 w-3.5" /> Join
      </span>
    </button>
  );
}

export function RecapRow({ bloq, onOpen }) {
  return (
    <button
      onClick={() => onOpen(bloq)}
      className="flex w-full items-center gap-3 rounded-2xl p-3 text-left transition-colors hover:bg-white/5"
      data-testid={`recap-row-${bloq.id}`}
    >
      <BloqEmoji emoji={bloq.emoji} icon={bloq.icon_b64} />
      <div className="min-w-0 flex-1">
        <span className="block truncate font-semibold">{bloq.name}</span>
        <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-bloq-muted">
          <Film className="h-3.5 w-3.5 shrink-0 text-bloq-purple" /> {bloq.recordings_count} recap{bloq.recordings_count === 1 ? "" : "s"} to catch up on
        </p>
      </div>
      <span className="shrink-0 rounded-full border border-white/12 px-3 py-1.5 text-xs font-semibold text-bloq-muted">Open</span>
    </button>
  );
}

// cc_23: BLOQ tab-pin toggle lives on POST /circles/{id}/pin-top → {pinned} (free limit 3 → 429 upsell).
function BloqPinToggle({ bloq }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.pinBloqTop(bloq.id),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ["bloqs"] }); toast.success(res?.pinned ? "Pinned to top" : "Unpinned"); },
    onError: (e) => { if (e?.response?.status !== 429) toast.error("Couldn't update pin"); },
  });
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); if (!mut.isPending) mut.mutate(); }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); mut.mutate(); } }}
      title={bloq.pinned ? "Unpin from top" : "Pin to top"}
      className={cn(
        "flex h-6 w-6 items-center justify-center rounded-full transition-opacity hover:bg-white/10",
        bloq.pinned ? "text-bloq-mint opacity-100" : "text-bloq-muted opacity-0 group-hover:opacity-100 focus:opacity-100",
      )}
      data-testid={`bloq-pin-toggle-${bloq.id}`}
    >
      {bloq.pinned ? <Pin className="h-3.5 w-3.5 fill-current" /> : <Pin className="h-3.5 w-3.5" />}
    </span>
  );
}

export function MyBloqRow({ bloq, activeId, onOpen }) {
  const tokenGate = MICROTX_ENABLED ? bloq.gate?.token_gate : null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(bloq)}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(bloq); }}
      className={cn(
        "group flex w-full cursor-pointer items-center gap-3 rounded-2xl p-3 text-left transition-colors",
        bloq.id === activeId ? "bg-white/8" : "hover:bg-white/5",
      )}
      data-testid={`my-bloq-${bloq.id}`}
    >
      <BloqEmoji emoji={bloq.emoji} icon={bloq.icon_b64} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-semibold">{bloq.name}</span>
          {bloq.active_room ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-destructive/40 bg-destructive/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-destructive" data-testid={`bloq-live-pill-${bloq.id}`}>
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-destructive" /> Live
            </span>
          ) : tokenGate ? (
            <TokenGatePill gate={tokenGate} className="shrink-0" />
          ) : (
            <GatePill minScore={bloq.gate?.min_score} requireVerified={bloq.gate?.require_verified} />
          )}
        </div>
        <p className="mt-0.5 truncate text-sm text-bloq-muted">
          {bloq.active_room ? `Live now · ${bloq.active_room.title}` : (bloq.last_message?.text || bloq.description)}
        </p>
      </div>
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-1">
          <BloqPinToggle bloq={bloq} />
          <span className="text-[11px] text-bloq-muted">{timeAgo(bloq.last_activity)}</span>
        </div>
        <BloqTopicUnread bloqId={bloq.id} unread={bloq.unread || 0} />
        {bloq.role === "owner" && (
          <span className="inline-flex items-center gap-0.5 rounded-md border border-bloq-purple/40 bg-bloq-purple/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-bloq-purple">
            <Crown className="h-2.5 w-2.5" /> Owner
          </span>
        )}
      </div>
    </div>
  );
}
